import type { ComponentProps, MouseEventHandler, UIEventHandler } from 'react';
import { Children, useEffect, useRef, useState } from 'react';

import * as S from './Swiper.layout';

// Tolérance pour la détection de la position du scroll
const SCROLL_THRESHOLD = 4;

type Props = Pick<ComponentProps<typeof S.Container>, 'className' | 'style' | 'children'> & {
  prevControl?: JSX.Element;
  nextControl?: JSX.Element;
  onScroll?: UIEventHandler<HTMLDivElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
};

export const Swiper = (props: Props) => {
  const { children, style, className } = props;
  const { prevControl, nextControl, onClick, onScroll } = props;

  const scrollableRef = useRef<HTMLDivElement>(null);
  const [isPrevHidden, setIsPrevHidden] = useState(true);
  const [isNextHidden, setIsNextHidden] = useState(true);

  // Préparation de la liste des éléments à afficher
  // Les éléments nuls ne sont ni wrappé, ni décomptés
  let count = 0;
  const items = Children.map(children, child => {
    if (child == null) return null;
    const style = { '--index': count++ };
    return <S.Item style={style}>{child}</S.Item>;
  });

  /**
   * Ce handler est déclenché lors du clic sur l'un des contrôles
   * La direction est disponible dans l'attribut `data-direction`
   */
  const handleClick: MouseEventHandler<HTMLDivElement> = e => {
    // Prérequis
    if (e.isTrusted) onClick?.(e);
    const scrollable = scrollableRef.current;
    const { direction } = e.currentTarget.dataset;
    if (scrollable == null || direction == null) return;

    // Largeur effective de scroll
    const style = getComputedStyle(scrollable);
    const clientWidth = scrollable.clientWidth;
    const paddingLeft = parseFloat(style.paddingLeft);
    const paddingRight = parseFloat(style.paddingRight);
    const effectiveWidth = clientWidth - paddingLeft - paddingRight;

    // Calcul de l'emplacement final désiré
    const scale = { prev: -1, next: 1 }[direction] ?? 0;
    const scrollBy = scale * effectiveWidth;
    const scrollTo = scrollable.scrollLeft + scrollBy;

    // Sous Safari, l'utilisation de `scrollBy` cause des décalages.
    // Ces décalages ne sont pas présent en utilisant `scrollTo`.
    scrollable.scrollTo({ left: scrollTo, behavior: 'smooth' });
  };

  /**
   * Ce handler est déclenché lors du scroll de la zone scrollable
   * Il met à jour la visibilité des contrôles de navigation
   */
  const handleScroll: UIEventHandler<HTMLDivElement> = e => {
    // Prérequis
    if (e.isTrusted) onScroll?.(e);
    if (!scrollableRef.current) return;

    // Calcul de la visibilité en fonction de la position du scroll
    const { scrollLeft, clientWidth, scrollWidth } = scrollableRef.current;
    const hiddenNext = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;
    const hiddenPrev = scrollLeft <= 0 + SCROLL_THRESHOLD;

    // Mise à jour des states
    setIsPrevHidden(hiddenPrev);
    setIsNextHidden(hiddenNext);
  };

  // Simulation d'un scroll pour mettre à jour la visibilité des contrôles.
  // Déclenché sans action utilisateur, il ne sera pas "trusted" dans le
  // handler, et ne déclenchera pas le handler `onScroll` des props.
  useEffect(() => {
    scrollableRef.current?.dispatchEvent(new Event('scroll'));
  }, []);

  return (
    <S.Container className={className} style={style}>
      <S.Content style={{ '--count': count }}>
        <S.Control data-direction="prev" aria-hidden={isPrevHidden} onClick={handleClick}>
          {prevControl ?? <S.Arrow name="chevron-left" color="ACTION_DEFAULT" />}
        </S.Control>
        <S.Scrollable ref={scrollableRef} role="list" onScroll={handleScroll}>
          {items}
          <S.Sizer />
        </S.Scrollable>
        <S.Control data-direction="next" aria-hidden={isNextHidden} onClick={handleClick}>
          {nextControl ?? <S.Arrow name="chevron-right" color="ACTION_DEFAULT" />}
        </S.Control>
      </S.Content>
    </S.Container>
  );
};
